import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
    CustomAuthPublicClientApplication,
    type ICustomAuthPublicClientApplication,
    ResetPasswordCodeRequiredState,
    ResetPasswordPasswordRequiredState,
    ResetPasswordCompletedState,
    type AuthFlowStateBase,
} from '@azure/msal-browser/custom-auth';
import { customAuthConfig } from '../../nativeAuthConfig';
import { authStyles, sanitizeInput, isValidEmail } from './authStyles';
import { NATIVE_AUTH_SESSION_KEY } from '../../components/LogoutButton';
import { completeNativeAuthSession } from '../../utils/nativeAuthComplete';

type Step = 'email' | 'otp' | 'password' | 'complete';

export default function ResetPasswordPage() {
    const navigate = useNavigate();
    const [authClient, setAuthClient] = useState<ICustomAuthPublicClientApplication | null>(null);
    const [step, setStep] = useState<Step>('email');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [focusedField, setFocusedField] = useState<string | null>(null);
    const [resetState, setResetState] = useState<AuthFlowStateBase | null>(null);

    // Fields
    const [email, setEmail] = useState('');
    const [emailError, setEmailError] = useState('');
    const [otpCode, setOtpCode] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [passwordError, setPasswordError] = useState('');

    // Initialize native auth client
    useEffect(() => {
        const init = async () => {
            try {
                const instance = await CustomAuthPublicClientApplication.create(customAuthConfig);
                setAuthClient(instance);
            } catch (err: any) {
                setError('Nepodarilo sa pripojiť k autentifikačnej službe.');
            }
        };
        init();
    }, []);

    // Step 1: Submit email to start reset flow
    const handleEmailSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setEmailError('');

        const cleanEmail = sanitizeInput(email.trim().toLowerCase(), 254);

        if (!cleanEmail) { setEmailError('Zadajte e-mailovú adresu.'); return; }
        if (!isValidEmail(cleanEmail)) { setEmailError('Neplatný formát e-mailovej adresy.'); return; }

        setLoading(true);

        if (!authClient) return;

        try {
            const result = await authClient.resetPassword({ username: cleanEmail });

            if (result.isFailed()) {
                if (result.error?.isUserNotFound()) {
                    setError('Účet s touto e-mailovou adresou neexistuje.');
                } else if (result.error?.isInvalidUsername()) {
                    setEmailError('Neplatná e-mailová adresa.');
                } else {
                    setError(getErrorMessage(result.error));
                }
                setLoading(false);
                return;
            }

            setResetState(result.state);

            if (result.state instanceof ResetPasswordCodeRequiredState) {
                setStep('otp');
            }
        } catch (err: any) {
            setError('Nastala chyba. Skúste to znova.');
        }
        setLoading(false);
    };

    // Step 2: Submit OTP code
    const handleOtpSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        if (!otpCode || otpCode.length < 4) {
            setError('Zadajte overovací kód z e-mailu.');
            return;
        }

        setLoading(true);

        try {
            if (resetState instanceof ResetPasswordCodeRequiredState) {
                const result = await resetState.submitCode(otpCode);

                if (result.isFailed()) {
                    if (result.error?.isInvalidCode()) {
                        setError('Neplatný overovací kód. Skontrolujte e-mail a skúste znova.');
                    } else {
                        setError(getErrorMessage(result.error));
                    }
                    setLoading(false);
                    return;
                }

                setResetState(result.state);

                if (result.state instanceof ResetPasswordPasswordRequiredState) {
                    setStep('password');
                }
            }
        } catch (err: any) {
            setError('Nastala chyba pri overovaní kódu. Skúste to znova.');
        }
        setLoading(false);
    };

    // Step 3: Submit new password
    const handlePasswordSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setPasswordError('');

        if (!newPassword) { setPasswordError('Zadajte nové heslo.'); return; }
        if (newPassword.length < 8) { setPasswordError('Heslo musí mať aspoň 8 znakov.'); return; }
        if (newPassword !== confirmPassword) { setPasswordError('Heslá sa nezhodujú.'); return; }

        setLoading(true);

        try {
            if (resetState instanceof ResetPasswordPasswordRequiredState) {
                const result = await resetState.submitNewPassword(newPassword);

                if (result.isFailed()) {
                    if (result.error?.isInvalidPassword()) {
                        setPasswordError('Heslo nespĺňa požiadavky. Skúste silnejšie heslo.');
                    } else {
                        setError(getErrorMessage(result.error));
                    }
                    setLoading(false);
                    return;
                }

                setResetState(result.state);

                if (result.state instanceof ResetPasswordCompletedState) {
                    setStep('complete');

                    // Auto sign-in after password reset
                    try {
                        const signInResult = await result.state.signIn();
                        if (signInResult.isCompleted()) {
                            await completeNativeAuthSession(email);
                            return;
                        }
                    } catch {
                        // Auto sign-in failed, user can sign in manually
                    }
                }
            }
        } catch (err: any) {
            setError('Nastala chyba pri nastavení hesla. Skúste to znova.');
        }
        setLoading(false);
    };

    const getErrorMessage = (error: any): string => {
        const desc = error?.errorData?.errorDescription;
        if (desc?.includes('throttl') || desc?.includes('rate')) return 'Príliš veľa pokusov. Počkajte minútu a skúste znova.';
        return 'Nastala neočakávaná chyba. Skúste to znova.';
    };

    const getInputStyle = (field: string, hasError: boolean = false) => ({
        ...authStyles.input,
        ...(hasError ? authStyles.inputError : {}),
        ...(focusedField === field && !hasError ? authStyles.inputFocused : {}),
    });

    const steps = ['email', 'otp', 'password'] as const;
    const stepIndex = { email: 0, otp: 1, password: 2, complete: 3 };

    const renderStepIndicator = () => (
        <div style={authStyles.stepIndicator}>
            {steps.map((s, i) => (
                <div key={s} style={authStyles.stepDot(step === s, stepIndex[step] > i)} />
            ))}
        </div>
    );

    // Step: Complete
    if (step === 'complete') {
        return (
            <div style={authStyles.page}>
                <div style={{ ...authStyles.card, textAlign: 'center' as const }}>
                    <div style={{ fontSize: '48px', marginBottom: '16px' }}>✅</div>
                    <h2 style={{ ...authStyles.logoTitle, fontSize: '22px' }}>Heslo bolo zmenené!</h2>
                    <p style={authStyles.subtitle}>Teraz sa môžete prihlásiť s novým heslom.</p>
                    <div style={{ marginTop: '24px' }}>
                        <Link
                            to="/auth/login"
                            style={{
                                ...authStyles.button,
                                display: 'inline-block',
                                textDecoration: 'none',
                                textAlign: 'center' as const,
                            }}
                        >
                            Prihlásiť sa
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    // Step: New password
    if (step === 'password') {
        return (
            <div style={authStyles.page}>
                <div style={authStyles.card}>
                    <div style={authStyles.logo}>
                        <div style={authStyles.logoIcon}>🔒</div>
                        <h1 style={{ ...authStyles.logoTitle, fontSize: '22px' }}>Nové heslo</h1>
                        <p style={authStyles.subtitle}>
                            Heslo musí mať aspoň 8 znakov, veľké a malé písmená a číslo.
                        </p>
                    </div>

                    {renderStepIndicator()}

                    <form onSubmit={handlePasswordSubmit} style={authStyles.form}>
                        <div style={authStyles.inputGroup}>
                            <label style={authStyles.label} htmlFor="new-password">Nové heslo</label>
                            <div style={authStyles.passwordWrapper as any}>
                                <input
                                    id="new-password"
                                    type={showPassword ? 'text' : 'password'}
                                    value={newPassword}
                                    onChange={(e) => { setNewPassword(e.target.value.slice(0, 128)); setPasswordError(''); }}
                                    onFocus={() => setFocusedField('new-password')}
                                    onBlur={() => setFocusedField(null)}
                                    style={getInputStyle('new-password', !!passwordError)}
                                    placeholder="Zadajte nové heslo"
                                    autoFocus
                                    autoComplete="new-password"
                                    maxLength={128}
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    style={authStyles.passwordToggle as any}
                                    tabIndex={-1}
                                    aria-label={showPassword ? 'Skryť heslo' : 'Zobraziť heslo'}
                                >
                                    {showPassword ? 'Skryť' : 'Ukázať'}
                                </button>
                            </div>
                            {passwordError && <span style={authStyles.fieldError}>{passwordError}</span>}
                        </div>

                        <div style={authStyles.inputGroup}>
                            <label style={authStyles.label} htmlFor="confirm-password">Potvrdenie hesla</label>
                            <div style={authStyles.passwordWrapper as any}>
                            <input
                                id="confirm-password"
                                type={showPassword ? 'text' : 'password'}
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value.slice(0, 128))}
                                onFocus={() => setFocusedField('confirm')}
                                onBlur={() => setFocusedField(null)}
                                style={getInputStyle('confirm', !!(confirmPassword && newPassword !== confirmPassword))}
                                placeholder="Zopakujte heslo"
                                autoComplete="new-password"
                                maxLength={128}
                            />
                            <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                style={authStyles.passwordToggle as any}
                                tabIndex={-1}
                                aria-label={showPassword ? 'Skryť heslo' : 'Zobraziť heslo'}
                            >
                                {showPassword ? 'Skryť' : 'Ukázať'}
                            </button>
                            </div>
                            {confirmPassword && newPassword !== confirmPassword && (
                                <span style={authStyles.fieldError}>Heslá sa nezhodujú.</span>
                            )}
                        </div>

                        <button
                            type="submit"
                            disabled={loading || !newPassword || !confirmPassword}
                            style={{
                                ...authStyles.button,
                                ...(loading || !newPassword || !confirmPassword ? authStyles.buttonDisabled : {}),
                            }}
                        >
                            {loading ? 'Nastavujem...' : 'Nastaviť nové heslo'}
                        </button>
                    </form>

                    {error && <div style={{ ...authStyles.error, marginTop: '16px' }}>{error}</div>}
                </div>
            </div>
        );
    }

    // Step: OTP code
    if (step === 'otp') {
        return (
            <div style={authStyles.page}>
                <div style={authStyles.card}>
                    <div style={authStyles.logo}>
                        <div style={authStyles.logoIcon}>📧</div>
                        <h1 style={{ ...authStyles.logoTitle, fontSize: '22px' }}>Overenie e-mailu</h1>
                        <p style={authStyles.subtitle}>
                            Na adresu <strong>{email}</strong> sme vám poslali overovací kód.
                        </p>
                    </div>

                    {renderStepIndicator()}

                    <form onSubmit={handleOtpSubmit} style={authStyles.form}>
                        <div style={authStyles.inputGroup}>
                            <label style={authStyles.label} htmlFor="reset-otp">Overovací kód</label>
                            <input
                                id="reset-otp"
                                type="text"
                                inputMode="numeric"
                                value={otpCode}
                                onChange={(e) => setOtpCode(e.target.value.replace(/[^0-9]/g, '').slice(0, 8))}
                                onFocus={() => setFocusedField('otp')}
                                onBlur={() => setFocusedField(null)}
                                style={{ ...getInputStyle('otp'), textAlign: 'center' as const, letterSpacing: '4px', fontSize: '20px' }}
                                placeholder="------"
                                autoFocus
                                autoComplete="one-time-code"
                                maxLength={8}
                            />
                        </div>

                        <button
                            type="submit"
                            disabled={loading || !otpCode}
                            style={{
                                ...authStyles.button,
                                ...(loading || !otpCode ? authStyles.buttonDisabled : {}),
                            }}
                        >
                            {loading ? 'Overujem...' : 'Overiť kód'}
                        </button>
                    </form>

                    {error && <div style={{ ...authStyles.error, marginTop: '16px' }}>{error}</div>}

                    <div style={{ ...authStyles.footer as any, fontSize: '13px' }}>
                        Neprišiel vám kód? Skontrolujte priečinok spam.
                    </div>
                </div>
            </div>
        );
    }

    // Step: Email (initial)
    return (
        <div style={authStyles.page}>
            <div style={authStyles.card}>
                <div style={authStyles.logo}>
                    <div style={authStyles.logoIcon}>🔑</div>
                    <h1 style={{ ...authStyles.logoTitle, fontSize: '22px' }}>Obnovenie hesla</h1>
                    <p style={authStyles.subtitle}>
                        Zadajte e-mailovú adresu, na ktorú vám pošleme overovací kód.
                    </p>
                </div>

                {renderStepIndicator()}

                <form onSubmit={handleEmailSubmit} style={authStyles.form}>
                    <div style={authStyles.inputGroup}>
                        <label style={authStyles.label} htmlFor="reset-email">E-mail</label>
                        <input
                            id="reset-email"
                            type="email"
                            value={email}
                            onChange={(e) => { setEmail(sanitizeInput(e.target.value, 254)); setEmailError(''); }}
                            onFocus={() => setFocusedField('email')}
                            onBlur={() => setFocusedField(null)}
                            style={getInputStyle('email', !!emailError)}
                            placeholder="vas@email.sk"
                            autoFocus
                            autoComplete="email"
                            maxLength={254}
                        />
                        {emailError && <span style={authStyles.fieldError}>{emailError}</span>}
                    </div>

                    <button
                        type="submit"
                        disabled={loading || !authClient}
                        style={{
                            ...authStyles.button,
                            ...(loading || !authClient ? authStyles.buttonDisabled : {}),
                        }}
                    >
                        {loading ? 'Odosielam...' : !authClient ? 'Pripájam sa...' : 'Odoslať overovací kód'}
                    </button>
                </form>

                {error && <div style={{ ...authStyles.error, marginTop: '16px' }}>{error}</div>}

                <div style={authStyles.footer as any}>
                    Späť na{' '}
                    <Link to="/auth/login" style={authStyles.link}>
                        prihlásenie
                    </Link>
                </div>
            </div>
        </div>
    );
}
